import React, { useState, useEffect, useContext } from "react";
import SideBarBox from "../Sidebar/SideBarBox";
import * as C from "./CaixinhaStyle";
import { AuthContext } from "../../context/AuthContext";
import { formatNumber } from "../../assets/utils";
import { goldenBoxService } from "../../Services/goldenBoxService";
import { contractService } from "../../Services/contractService";
import {
  FaGift,
  FaPlus,
  FaMoneyBillWave,
  FaCalendarAlt,
  FaPercentage,
  FaSpinner,
  FaCheckCircle,
  FaInfoCircle,
  FaSearch,
  FaTimes,
} from "react-icons/fa";
import ModalVisualisacaoCaixinha from "./ModalVisualisacaoCaixinha/ModalVisualisacaoCaixinha";
import NewGoldenBox from "./NewGoldenBox/NewGoldenBox";
import { useLocation } from "react-router-dom";
import ModalSaque from "./ModalSaque/ModalSaque";
import TabelaSaques from "./TabelaSaques/TabelaSaques";
import TabelaExtratos from "./TabelaExtratos/TabelaExtratos";

export default function Caixinha() {
  const { userData } = useContext(AuthContext);
  const location = useLocation();

  const [dashboardData, setDashboardData] = useState(null);
  const [investmentsList, setInvestmentsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSaqueModalOpen, setIsSaqueModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("contracts");

  const [selectedBoxToView, setSelectedBoxToView] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [initialContractIdFromUrl, setInitialContractIdFromUrl] =
    useState(null);

  const [isEligibilityModalOpen, setIsEligibilityModalOpen] = useState(false);
  const [eligibilityType, setEligibilityType] = useState(null);
  const [eligibilityData, setEligibilityData] = useState([]);
  const [loadingEligibility, setLoadingEligibility] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const activate = params.get("activateGoldenBox");
    const contractId = params.get("contractId");

    if (activate === "true" && contractId) {
      setInitialContractIdFromUrl(Number(contractId));
      setIsAddModalOpen(true);
    }
  }, [location]);

  const handleOpenViewModal = (box) => {
    setSelectedBoxToView(box);
    setIsViewModalOpen(true);
  };

  const fetchDashboard = async () => {
    if (userData?.id) {
      try {
        setLoading(true);
        const data = await goldenBoxService.getDashboardData(userData.id);
        setDashboardData(data);

        const boxesAnuais = data.saqueAnualInfo?.goldenBoxes || [];
        const boxesFim = data.saqueNoFimInfo?.goldenBoxes || [];

        let allBoxes = [...boxesAnuais, ...boxesFim];

        // Mapeamento de prioridade para ordenação
        // ACTIVE (1) > PENDING (0 ou "PENDING") > Outros
        const statusPriority = {
          ACTIVE: 1,
          PENDING: 2,
          FINISHED: 3,
          REJECTED: 4,
          CANCELED: 5,
          1: 1, // Caso venha como número (Active)
          0: 2, // Caso venha como número (Pending - ajuste se seu enum for diferente)
          2: 4, // Rejected
          3: 3, // Finished
          4: 5, // Canceled
        };

        allBoxes.sort((a, b) => {
          // Pega a prioridade (se não existir, joga pro fim com 99)
          const priorityA =
            statusPriority[a.status] ||
            statusPriority[String(a.status).toUpperCase()] ||
            99;
          const priorityB =
            statusPriority[b.status] ||
            statusPriority[String(b.status).toUpperCase()] ||
            99;

          // Se prioridade for igual, ordena por data (mais recente primeiro)
          if (priorityA === priorityB) {
            return new Date(b.dateCreated) - new Date(a.dateCreated);
          }
          return priorityA - priorityB;
        });

        setInvestmentsList(allBoxes);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, [userData]);

  const handleCheckEligibility = async (type) => {
    if (!userData?.id) return;
    setEligibilityType(type);
    setIsEligibilityModalOpen(true);
    setLoadingEligibility(true);
    setEligibilityData([]);

    try {
      const data = await contractService.getAvailabilityAnalysis(userData.id);
      setEligibilityData(data);
    } catch (error) {
      console.error("Erro ao buscar elegibilidade", error);
    } finally {
      setLoadingEligibility(false);
    }
  };

  const totalBalance = dashboardData?.totalAmountAtGoldenBox || 0;

  const availableToWithdraw =
    (dashboardData?.saqueAnualInfo?.totalAmountAvaliable || 0) +
    (dashboardData?.saqueNoFimInfo?.totalAmountAvaliable || 0);

  const getNextWithdrawDate = (datesList) => {
    if (!datesList || datesList.length === 0) return "Sem previsão";
    const today = new Date();
    const sortedDates = datesList.map((d) => new Date(d)).sort((a, b) => a - b);
    const nextDate = sortedDates.find((d) => d >= today);
    if (nextDate) return nextDate.toLocaleDateString("pt-BR");
    return "Finalizado";
  };

  const getStatusInfo = (status) => {
    const s = String(status).toUpperCase();
    switch (s) {
      case "PENDING":
      case "0":
        return { label: "Pendente", color: "#f59e0b", bg: "#fffbeb" }; // Amarelo
      case "ACTIVE":
      case "1":
        return { label: "Ativo", color: "#10b981", bg: "#ecfdf5" }; // Verde
      case "FINISHED":
      case "3":
        return { label: "Finalizado", color: "#3b82f6", bg: "#eff6ff" }; // Azul
      case "REJECTED":
      case "2":
      case "4": // Ajuste conforme seu enum
        return { label: "Rejeitado", color: "#ef4444", bg: "#fef2f2" }; // Vermelho
      case "CANCELED":
      case "5":
        return { label: "Cancelado", color: "#6b7280", bg: "#f9fafb" }; // Cinza
      default:
        return { label: status, color: "#6b7280", bg: "#f3f4f6" };
    }
  };

  const filteredEligibilityList = eligibilityData.filter((contract) => {
    if (eligibilityType === "ANNUAL")
      return contract.avaliableForAnualGoldenBoxType;
    if (eligibilityType === "END") return contract.avaliableForEndGoldenBoxType;
    return false;
  });

  if (loading) {
    return (
      <SideBarBox>
        <C.Container
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <FaSpinner
            className="spinner"
            size={40}
            color="#D4AF37"
            style={{ animation: "spin 1s linear infinite" }}
          />
          <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        </C.Container>
      </SideBarBox>
    );
  }

  return (
    <SideBarBox>
      <C.Container>
        <C.Header>
          <h1>
            <FaGift color="#D4AF37" /> Golden Box - Caixa de Ouro
          </h1>
          <p>
            Gerencie suas aplicações, maximize suas valorizações e planeje seus
            saques.
          </p>
        </C.Header>

        <C.SummaryGrid>
          <C.SummaryCard dark>
            <FaGift className="icon-bg" />
            <span>Saldo Total na Caixinha</span>
            <h2>R$ {formatNumber(totalBalance)}</h2>
          </C.SummaryCard>

          <C.SummaryCard>
            <FaMoneyBillWave className="icon-bg" />
            <span>Saldo Disponível para Saque</span>
            <h2
              style={{
                color: availableToWithdraw > 0 ? "#10b981" : "#111827",
              }}
            >
              R$ {formatNumber(availableToWithdraw)}
            </h2>
          </C.SummaryCard>
        </C.SummaryGrid>

        <C.OptionsSection>
          <h3>
            <FaInfoCircle size={18} style={{ marginRight: 8 }} /> Modalidades
            Disponíveis
          </h3>
          <C.OptionsGrid>
            <C.OptionCard>
              <div className="card-content">
                <div className="icon-header">
                  <FaCalendarAlt size={24} color="#D4AF37" />
                  <span className="title">Plano Anual</span>
                </div>
                <p className="desc">
                  Ideal para contratos com mais de 12 meses. Equilibra
                  valorização com liquidez anual.
                </p>
                <ul>
                  <li>
                    <FaCheckCircle size={12} color="#10b981" />
                    <span>
                      <strong>1% de bônus mensal</strong> (saque mensal).
                    </span>
                  </li>
                  <li>
                    <FaCheckCircle size={12} color="#10b981" />
                    <span>
                      Saque da valorização após <strong>12 meses</strong>.
                    </span>
                  </li>
                </ul>
              </div>
              <C.CheckButton onClick={() => handleCheckEligibility("ANNUAL")}>
                <FaSearch /> Verificar meus contratos elegíveis
              </C.CheckButton>
            </C.OptionCard>

            <C.OptionCard>
              <div className="card-content">
                <div className="icon-header">
                  <FaMoneyBillWave size={24} color="#D4AF37" />
                  <span className="title">Plano Total</span>
                </div>
                <p className="desc">
                  Máxima valorização mensal para contratos de longo prazo, ideal
                  para quem tem no mínimo 2 anos de contrato.
                </p>
                <ul>
                  <li>
                    <FaCheckCircle size={12} color="#10b981" />
                    <span>
                      <strong>1.3% de bônus mensal</strong> (saque mensal).
                    </span>
                  </li>
                  <li>
                    <FaCheckCircle size={12} color="#10b981" />
                    <span>
                      Saque da valorização no <strong>fim do contrato</strong>.
                    </span>
                  </li>
                </ul>
              </div>
              <C.CheckButton onClick={() => handleCheckEligibility("END")}>
                <FaSearch /> Verificar meus contratos elegíveis
              </C.CheckButton>
            </C.OptionCard>
          </C.OptionsGrid>
        </C.OptionsSection>

        <C.ActionsBar>
          <C.TabContainer>
            <C.TabButton
              active={activeTab === "contracts"}
              onClick={() => setActiveTab("contracts")}
            >
              Meus Contratos Aplicados
            </C.TabButton>
            <C.TabButton
              active={activeTab === "withdraws"}
              onClick={() => setActiveTab("withdraws")}
            >
              Minhas Retiradas
            </C.TabButton>
            <C.TabButton
              active={activeTab === "extracts"}
              onClick={() => setActiveTab("extracts")}
            >
              Extrato
            </C.TabButton>
          </C.TabContainer>

          <div className="buttons">
            <C.ActionButton
              onClick={() => setIsSaqueModalOpen(true)}
              disabled={availableToWithdraw <= 0}
              title={
                availableToWithdraw <= 0
                  ? "Sem saldo disponível para saque"
                  : "Realizar saque"
              }
            >
              <FaMoneyBillWave /> RETIRAR SALDO DISPONÍVEL
            </C.ActionButton>

            <C.ActionButton
              primary
              onClick={() => {
                setInitialContractIdFromUrl(null);
                setIsAddModalOpen(true);
              }}
            >
              <FaPlus /> ADICIONAR CONTRATOS
            </C.ActionButton>
          </div>
        </C.ActionsBar>

        {activeTab === "contracts" && (
          <C.ContentWrapper key="contracts">
            <C.InvestmentsGrid>
              {investmentsList.length === 0 ? (
                <div
                  style={{
                    gridColumn: "1/-1",
                    textAlign: "center",
                    padding: "40px",
                    color: "#6b7280",
                  }}
                >
                  Você ainda não possui contratos na Caixinha Golden.
                </div>
              ) : (
                investmentsList.map((box) => {
                  const isMonthly = box.goldenBoxOptionId === 1;
                  const valorAtual = box.totalAccumulated || 0;
                  const valorDisponivel = box.currentAmountAvaliable || 0;
                  const proximoSaqueData = getNextWithdrawDate(
                    box.nextWithdrawDates
                  );
                  const statusInfo = getStatusInfo(box.status);

                  return (
                    <C.InvestmentCard
                      key={box.id}
                      type={isMonthly ? "monthly" : "annual"}
                      onClick={() => handleOpenViewModal(box)}
                      style={{
                        cursor: "pointer",
                        opacity: statusInfo.label === "Ativo" ? 1 : 0.7,
                      }}
                    >
                      <div className="header">
                        <div className="top-row">
                          <span className="id">
                            ID: #{box.id} - Contrato #{box.contractId}
                          </span>
                          <C.StatusBadge
                            color={statusInfo.color}
                            bg={statusInfo.bg}
                          >
                            {statusInfo.label}
                          </C.StatusBadge>
                        </div>
                        <span className="badge">
                          {isMonthly
                            ? "Fim + 1.3% Mensal"
                            : "Anual + 1% Mensal"}
                        </span>
                      </div>

                      <div className="balance-section">
                        <label>Saldo Acumulado</label>
                        <h3>R$ {formatNumber(valorAtual)}</h3>
                      </div>

                      <div className="info-row">
                        <span>
                          <FaCalendarAlt style={{ marginRight: 5 }} /> Próximo
                          Saque:
                        </span>
                        <strong>{proximoSaqueData}</strong>
                      </div>

                      <div className="info-row">
                        <span>
                          <FaPercentage style={{ marginRight: 5 }} /> Disp.
                          Saque:
                        </span>
                        <strong className="highlight">
                          R$ {formatNumber(valorDisponivel)}
                        </strong>
                      </div>
                    </C.InvestmentCard>
                  );
                })
              )}
            </C.InvestmentsGrid>
          </C.ContentWrapper>
        )}

        {activeTab === "withdraws" && (
          <C.ContentWrapper key="withdraws">
            <TabelaSaques onUpdateDashboard={fetchDashboard} />
          </C.ContentWrapper>
        )}

        {activeTab === "extracts" && (
          <C.ContentWrapper key="extracts">
            <TabelaExtratos />
          </C.ContentWrapper>
        )}
      </C.Container>

      {isEligibilityModalOpen && (
        <C.ModalOverlay onClick={() => setIsEligibilityModalOpen(false)}>
          <C.ModalContent onClick={(e) => e.stopPropagation()}>
            <C.ModalHeader>
              <h2>
                Contratos Elegíveis -{" "}
                {eligibilityType === "ANNUAL" ? "Plano Anual" : "Plano Total"}
              </h2>
              <button onClick={() => setIsEligibilityModalOpen(false)}>
                <FaTimes />
              </button>
            </C.ModalHeader>
            <C.ModalBody>
              {loadingEligibility ? (
                <div style={{ textAlign: "center", padding: "40px" }}>
                  <FaSpinner
                    className="spinner"
                    size={30}
                    color="#D4AF37"
                    style={{ animation: "spin 1s linear infinite" }}
                  />
                  <p style={{ marginTop: 10, color: "#666" }}>
                    Verificando seus contratos...
                  </p>
                </div>
              ) : filteredEligibilityList.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "30px",
                    color: "#6b7280",
                  }}
                >
                  <FaInfoCircle
                    size={40}
                    style={{ marginBottom: 15, opacity: 0.5 }}
                  />
                  <p>
                    Nenhum contrato ativo se encaixa nos critérios desta
                    modalidade no momento.
                  </p>
                </div>
              ) : (
                <C.EligibilityList>
                  {filteredEligibilityList.map((c) => (
                    <C.EligibilityItem key={c.contractId}>
                      <div className="left-info">
                        <div className="contract-id">
                          Contrato #{c.contractId}
                        </div>
                        <div className="months">
                          Faltam {c.monthsToEnd} meses
                        </div>
                      </div>
                      <div className="right-info">
                        <div className="value">
                          R$ {formatNumber(c.contractTotalPrice)}
                        </div>
                        <div className="status">
                          Elegível <FaCheckCircle />
                        </div>
                      </div>
                    </C.EligibilityItem>
                  ))}
                </C.EligibilityList>
              )}
            </C.ModalBody>
            <C.ModalFooter>
              <C.ActionButton
                primary
                onClick={() => {
                  setIsEligibilityModalOpen(false);
                  setInitialContractIdFromUrl(null);
                  setIsAddModalOpen(true);
                }}
              >
                <FaPlus /> CRIAR NOVA CAIXINHA
              </C.ActionButton>
            </C.ModalFooter>
          </C.ModalContent>
        </C.ModalOverlay>
      )}

      <ModalVisualisacaoCaixinha
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        data={selectedBoxToView}
      />
      {/* 
      <NewGoldenBox
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        initialContractId={initialContractIdFromUrl}
        onSuccess={fetchDashboard}
      /> */}

      <NewGoldenBox
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setInitialContractIdFromUrl(null); // Limpa o ID ao fechar para evitar re-execuções
        }}
        initialContractId={initialContractIdFromUrl}
        onSuccess={() => {
          fetchDashboard();
          setInitialContractIdFromUrl(null); // Limpa também no sucesso
        }}
      />

      <ModalSaque
        isOpen={isSaqueModalOpen}
        onClose={() => setIsSaqueModalOpen(false)}
        data={investmentsList}
        onSuccess={fetchDashboard}
      />
    </SideBarBox>
  );
}
